/**
 * SHARED COLUMN SELECTOR
 *
 * Controls AG Grid column visibility and supports an optional trigger class for
 * layout-specific sizing without changing the shared dropdown behavior.
 *
 * Changes:
 * - 2026-09-01: Allows opt-in trigger sizing for compact page headers.
 */

import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ColDef } from 'ag-grid-community';

interface ColumnSelectorProps<T> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  columnDefs: ColDef<T>[];
  orderedColumns: ColDef<T>[];
  onStateUpdated: (newDefs: ColDef<T>[]) => void;
  triggerClassName?: string;
}

function ColumnSelector<T>({
  isOpen,
  setIsOpen,
  columnDefs,
  orderedColumns,
  onStateUpdated,
  triggerClassName,
}: ColumnSelectorProps<T>): JSX.Element {
  const selectorRef = useRef<HTMLDivElement>(null);
  // Get a consistent identifier for a column
  const getColumnId = useCallback((col: ColDef<T>): string => {
    return col.colId || col.field || 'unnamed-column';
  }, []);

  // Calculate visible fields count
  const visibleFields = orderedColumns.filter(col => !col.hide).length;


  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        dropdownRef.current &&
        !selectorRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen]);

  const handleColumnVisibilityChange = useCallback((columnId: string, visible: boolean) => {
    const isTitle = columnId === 'title';
    if (isTitle) return;

    try {
      const updatedDefs = orderedColumns.map(def => {
        const defIdentifier = def.colId || def.field;
        if (defIdentifier === columnId) {
          return {
            ...def,
            hide: !visible, // Set hide to opposite of visible
            pinned: defIdentifier === 'title' ? 'left' as const : def.pinned
          };
        }
        return def;
      });

      // Call parent handler with updated definitions
      onStateUpdated(updatedDefs);
    } catch (error) {
      console.error('ColumnSelector - Error updating column visibility:', error);
    }
  }, [columnDefs, orderedColumns, onStateUpdated]);

  return (
    <div className="relative shrink-0" ref={selectorRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center px-2 py-1.5 rounded-md text-xs font-medium leading-6 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${triggerClassName || ''}`}
        title="Configure columns visibility"
        aria-label="Toggle column selection"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 0 1 1.414 0L10 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414z"
            clipRule="evenodd"
          />
        </svg>
        <span className="ml-1.5">Columns ({visibleFields}/{columnDefs.length})</span>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: selectorRef.current ? selectorRef.current.getBoundingClientRect().bottom + 4 : 0,
            left: selectorRef.current ? selectorRef.current.getBoundingClientRect().left : 0
          }}
          className="z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
        >
          <div className="p-2 space-y-1 divide-y divide-gray-200 dark:divide-gray-700">
            <div className="flex items-center justify-between pb-2">
              <button
                onClick={() => {
                  try {
                    // Create new column definitions with all columns visible
                    const newState = orderedColumns.map(col => {
                      const isTitle = getColumnId(col) === 'title';
                      return {
                        ...col,
                        hide: false,
                        pinned: isTitle ? 'left' as const : undefined
                      };
                    });
                    onStateUpdated(newState);
                  } catch (error) {
                    console.error('ColumnSelector - Error in Show All:', error);
                  }
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Show All
              </button>
              <button
                onClick={() => {
                  try {
                    // Create new column definitions with default settings
                    const newState = orderedColumns.map((col) => {
                      const isTitle = getColumnId(col) === 'title';
                      return {
                        ...col,
                        hide: !isTitle,
                        width: isTitle ? 300 :
                          col.type === 'date' ? 150 :
                            col.type === 'number' ? 120 : 180,
                        pinned: isTitle ? 'left' as const : undefined
                      };
                    });
                    onStateUpdated(newState);
                  } catch (error) {
                    console.error('ColumnSelector - Error in Reset:', error);
                  }
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Reset
              </button>
            </div>

            <div className="pt-2 space-y-1 max-h-64 overflow-y-auto">
              {orderedColumns.map((column, index) => {
                const isVisible = !column.hide;
                const columnId = getColumnId(column);

                const isTitle = columnId === 'title';
                return (
                  <div
                    key={`column-${index}-${columnId || 'unnamed'}`}
                    className="flex items-center px-2 py-1 rounded select-none hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <label className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        checked={isVisible}
                        disabled={isTitle}
                        onChange={(e) => handleColumnVisibilityChange(columnId, e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-200">{column.headerName || columnId}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColumnSelector;
